import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { api } from '../../api/client';
import { useAuth } from '../../stores/AuthContext';
import styles from './Dashboard.module.css';

export default function SuperAdminDashboard() {
  const { user, logout } = useAuth();
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const location = useLocation();
  const isChildRoute = location.pathname !== '/super-admin';

  const navItems = [
    { href: '/super-admin', label: 'Dashboard', icon: '📊' },
    { href: '/super-admin/schools', label: 'Escuelas', icon: '🏫' },
    ...(isSuperAdmin ? [{ href: '/super-admin/users', label: 'Administradores', icon: '👥' }] : []),
    { href: '/super-admin/profile', label: 'Mi Perfil', icon: '👤' },
  ];

  const [schools, setSchools] = useState<any[]>([]);
  const [stats, setStats] = useState({
    totalSchools: 0,
    totalUsers: 0,
    totalStudents: 0,
    totalGroups: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get<any[]>('/api/super-admin/schools')
      .then((data) => {
        if (Array.isArray(data)) {
          setSchools(data);
          setStats({
            totalSchools: data.length,
            totalUsers: data.reduce((s: number, x: any) => s + x._count.users, 0),
            totalStudents: data.reduce((s: number, x: any) => s + x._count.students, 0),
            totalGroups: data.reduce((s: number, x: any) => s + x._count.groups, 0),
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleLogout = async () => {
    await logout();
    window.location.href = '/super-admin/login';
  };

  return (
    <div className={styles.layout}>
      <aside className={styles.sidebar}>
        <div className={styles.logo}>CtrlEscolar</div>
        <div className={styles.subtitle}>Super Administrador</div>
        <nav className={styles.nav}>
          {navItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className={`${styles.navItem} ${location.pathname === item.href ? styles.navItemActive : ''}`}
            >
              <span>{item.icon}</span>
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>
        <div className={styles.spacer} />
        <button onClick={handleLogout} className={styles.logoutBtn}>
          Cerrar Sesión
        </button>
      </aside>
      <main className={styles.main}>
        <div className={styles.header}>
          <span className={styles.headerTitle}>Panel de Control</span>
          <span className={styles.headerUser}>{user?.name || 'Super Admin'}</span>
        </div>
        <div className={styles.content}>
          {isChildRoute ? (
            <Outlet />
          ) : (
            <div className={styles.container}>
              <div className={styles.statsGrid}>
                <div className={styles.statCard}>
                  <div className={styles.statLabel}>Escuelas</div>
                  <div className={styles.statValue}>{stats.totalSchools}</div>
                  <div className={styles.statSub}>Registradas en el sistema</div>
                </div>
                <div className={styles.statCard}>
                  <div className={styles.statLabel}>Usuarios</div>
                  <div className={styles.statValue}>{stats.totalUsers}</div>
                  <div className={styles.statSub}>En todas las instituciones</div>
                </div>
                <div className={styles.statCard}>
                  <div className={styles.statLabel}>Alumnos</div>
                  <div className={styles.statValue}>{stats.totalStudents}</div>
                  <div className={styles.statSub}>Matriculados</div>
                </div>
                <div className={styles.statCard}>
                  <div className={styles.statLabel}>Grupos</div>
                  <div className={styles.statValue}>{stats.totalGroups}</div>
                  <div className={styles.statSub}>Activos</div>
                </div>
              </div>

              <div className={styles.section}>
                <h2 className={styles.sectionTitle}>Instituciones Registradas</h2>
                <div className={styles.tableCard}>
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Institución</th>
                        <th>Slug</th>
                        <th>Plan</th>
                        <th>Usuarios</th>
                        <th>Alumnos</th>
                        <th>Grupos</th>
                        <th>Estatus</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={7} style={{ textAlign: 'center', padding: 20 }}>
                            Cargando...
                          </td>
                        </tr>
                      ) : schools.length === 0 ? (
                        <tr>
                          <td colSpan={7} style={{ textAlign: 'center', padding: 20 }}>
                            Sin escuelas registradas
                          </td>
                        </tr>
                      ) : (
                        schools.map((s: any) => (
                          <tr key={s.id}>
                            <td>
                              <strong>{s.name}</strong>
                            </td>
                            <td>{s.slug}</td>
                            <td>
                              <span className={styles.proBadge}>
                                {s.plan === 'pro' ? 'Pro' : 'Básico'}
                              </span>
                            </td>
                            <td>{s._count.users}</td>
                            <td>{s._count.students}</td>
                            <td>{s._count.groups}</td>
                            <td>
                              <span className={styles.activeBadge}>
                                {s.isActive ? 'Activo' : 'Inactivo'}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className={styles.version}>
                CtrlEscolar v1.0.0 — Plataforma de Gestión Escolar Multi-Tenant
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
